import moment from 'moment';
import React, { ReactNode, type JSX } from 'react';

import {
    toggleScheduleViewMode,
    toggleScheduleGrouping,
    setSchedulesForComponent,
    setHoveredScheduleFilterDate,
    setSelectedScheduleFilterDate,
    setInit,
} from '@fiji/common/src/features/component/schedule/scheduleSlice';

import { DatePicker, DateRangeIcon } from '@mui/x-date-pickers-pro';
import { CustomDayPicker } from '../WeekView';
import { useAppDispatch, useTypedSelector } from '@fiji/common';
import { Stack, Switch, Typography, ToggleButton, ToggleButtonGroup, IconButton, Button, Box } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { CustomTransComponent } from 'components/CustomTransComponent';
import { useTransformData } from 'hooks/useTransformData';

type ComponentProps = {
    groupingEnabled?: boolean;
    rightComponents?: Array<{ [key: string]: unknown }>;
} & (ComponentPropsBase | GroupingProps);

type ComponentPropsBase = {
    groupingEnabled?: false;
};

type GroupingProps = {
    groupingEnabled: true;
    groupingLabel: ReactNode;
};

export const ScheduleHeader = (props: ComponentProps): JSX.Element => {
    const { groupingEnabled, rightComponents } = props;

    const dispatch = useAppDispatch();
    const dateRef = React.useRef<HTMLInputElement>(null);

    const mode = useTypedSelector((state: any) => state['schedule']['mode']);
    const selectedDate = useTypedSelector((state: any) => state['schedule']['selectedDate']);
    const isGroupingEnabled = useTypedSelector((state: any) => state['schedule']['isGroupingEnabled']);
    const { calendarWeek, timezone } = useTransformData();

    const getDaySlot = (dayProps: any): JSX.Element => <CustomDayPicker {...dayProps} />;
    const color = useTypedSelector((state: any) => state?.themePalette?.selectedThemePalette);

    const handlePrev = (): void => {
        const newDate = moment(selectedDate).subtract(1, mode === 'week' ? 'week' : 'day');
        dispatch(setSelectedScheduleFilterDate(newDate.toISOString()));
    };

    const handleNext = (): void => {
        const newDate = moment(selectedDate).add(1, mode === 'week' ? 'week' : 'day');
        dispatch(setSelectedScheduleFilterDate(newDate.toISOString()));
    };

    React.useEffect(() => {
        if (timezone) dispatch(setInit({ timezone }));
    }, [timezone]);

    return (
        <Stack direction={'row'} justifyContent={'space-between'}>
            <Stack direction={'row'} spacing={3} alignItems={'center'}>
                <ToggleButtonGroup
                    value={mode}
                    exclusive
                    onChange={(e: React.ChangeEvent<any>): void => {
                        dispatch(toggleScheduleViewMode({ mode: e.target.value, startOfWeek: calendarWeek, timezone }));
                        dispatch(setSchedulesForComponent([]));
                    }}
                    aria-label="text alignment"
                >
                    <ToggleButton sx={{ textTransform: 'none' }} value="day" aria-label="centered">
                        <CustomTransComponent translationKey={'COMMON:DAY'} />
                    </ToggleButton>
                    <ToggleButton sx={{ textTransform: 'none' }} value="week" aria-label="centered">
                        <CustomTransComponent translationKey={'COMMON:WEEK'} />
                    </ToggleButton>
                </ToggleButtonGroup>

                <Stack direction="row" alignItems="center" justifyContent={'space-between'}>
                    {/* Left Arrow */}
                    <IconButton onClick={handlePrev}>
                        <ArrowBackIos fontSize="small" color="primary" />
                    </IconButton>

                    {/* Date Picker (no icon inside) */}
                    <Box sx={{ width: '200px' }}>
                        <DatePicker
                            inputRef={dateRef}
                            value={moment(selectedDate)}
                            format="LL"
                            onChange={(date) => {
                                const newDate = mode === 'week' ? moment(date).startOf('week') : moment(date);
                                dispatch(setSelectedScheduleFilterDate(newDate.toISOString()));
                            }}
                            slots={mode === 'week' ? { day: getDaySlot } : undefined}
                            slotProps={
                                mode === 'week'
                                    ? {
                                          day: (ownerState) => ({
                                              selectedDay: moment(selectedDate),
                                              onPointerEnter: () =>
                                                  dispatch(
                                                      setHoveredScheduleFilterDate(
                                                          moment(ownerState?.day).toISOString()
                                                      )
                                                  ),
                                              onPointerLeave: () => dispatch(setHoveredScheduleFilterDate(null)),
                                          }),
                                      }
                                    : undefined
                            }
                        />
                    </Box>

                    {/* Right Arrow */}
                    <IconButton onClick={handleNext}>
                        <ArrowForwardIos fontSize="small" color="primary" />
                    </IconButton>

                    {/* Today Button with calendar icon */}
                    <Button
                        sx={{
                            borderWidth: 1,
                            borderColor: '#424e541f',
                            borderStyle: 'solid',
                            padding: '10px 24px',
                            backgroundColor: mode === 'week' ? color?.[500] : '',
                            color: mode !== 'week' ? color?.[500] : 'grey',
                            cursor: mode === 'week' ? 'not-allowed' : '',
                        }}
                        disabled={mode === 'week'}
                        value="today"
                        aria-label="today"
                        className={`${mode === 'week' ? '' : 'primary'}`}
                    >
                        <DateRangeIcon fontSize="small" />
                        Today
                    </Button>
                </Stack>
            </Stack>
            {groupingEnabled && (
                <Stack alignItems={'center'} direction={'row'} spacing={1}>
                    <Typography variant="subtitle1" fontSize={'16px'} fontWeight={'600'}>
                        {props.groupingLabel}
                    </Typography>
                    <Switch checked={isGroupingEnabled} onChange={() => dispatch(toggleScheduleGrouping())} />
                </Stack>
            )}
            {mode === 'week' && rightComponents?.length && (
                <Stack direction={'row'} spacing={2} sx={{ width: '100%' }} justifyContent="flex-end">
                    {rightComponents?.map((componentDetails: any) => {
                        const RightComponent = componentDetails?.component;
                        return <RightComponent key={componentDetails.id} {...componentDetails} />;
                    })}
                </Stack>
            )}
        </Stack>
    );
};
